import { Controller } from "@hotwired/stimulus"

// Attached to each rendered block in admin_edit mode. Owns:
//  - hover toolbar visibility (CSS-driven; controller just injects markup)
//  - click-to-edit (delegates to existing slide edit view; opens in the
//    Turbo "slide-over" frame when present, falls back to a full-page nav)
//  - "change type" trigger (opens admin-section-palette popover anchored here)
//  - delete trigger (DELETE on the slide via the helper-provided URL)
//
// Mounted by InnerBodyAdminChromeHelper#inner_body_admin_chrome which sets:
//   data-controller="admin-block"
//   data-slide-id, data-section-type, data-section-label, data-palette
//   data-admin-block-edit-url-value, data-admin-block-destroy-url-value
export default class extends Controller {
  static targets = ["body", "stateSelect"]
  static values = { editUrl: String, destroyUrl: String }

  connect() {
    this._buildToolbar()
    // The static_home layouts declare `turbo_refreshes_with method:
    // :morph`, so same-URL Turbo.visits and `turbo_stream_from`
    // broadcasts both reach this element through idiomorph. Morph
    // preserves the wrapper (matched by `id="admin-block-slide-N"`)
    // but strips the JS-injected `.sh-admin-block-toolbar` child as
    // an unexpected node — and connect() doesn't re-fire on a
    // morph-preserved element, so we re-build from the morph event.
    this._onMorphElement = this._onMorphElement.bind(this)
    this.element.addEventListener("turbo:morph-element", this._onMorphElement)
  }

  disconnect() {
    this.element.removeEventListener("turbo:morph-element", this._onMorphElement)
  }

  _onMorphElement(event) {
    // The event bubbles from morphed descendants; only react when our
    // own wrapper is the morph target.
    if (event.target !== this.element) return
    this._buildToolbar()
  }

  _buildToolbar() {
    // Guard against Turbo back/forward or page-cache restore re-invoking
    // connect() on the same element and stacking toolbars.
    if (this.element.querySelector(":scope > .sh-admin-block-toolbar")) return
    const toolbar = document.createElement("div")
    toolbar.className = "sh-admin-block-toolbar"
    toolbar.innerHTML = `
      <button type="button" class="sortable-slide-handle" title="Drag to reorder" aria-label="Drag to reorder section">⋮⋮</button>
      <button type="button" data-action="click->admin-block#openEditor">Edit</button>
      <button type="button" data-action="click->admin-block#openTypePalette">Type ▾</button>
      <select name="slide[aasm_state]" aria-label="Section publication state" data-admin-block-target="stateSelect" data-action="change->admin-block#changeState" title="Change section publication state">
        <option value="draft">Draft</option>
        <option value="published">Published</option>
        <option value="unpublished">Unpublished</option>
      </select>
      <button type="button" data-action="click->admin-block#delete" class="sh-admin-danger">Delete</button>
    `
    this.element.appendChild(toolbar)
    const select = toolbar.querySelector("[data-admin-block-target='stateSelect']")
    const initial = this.element.dataset.aasmState || "draft"
    select.value = initial
    this._restyleState(select, initial)
  }

  async changeState(event) {
    const select = event.currentTarget
    const newState = select.value
    const previousState = this.element.dataset.aasmState || "draft"
    const csrfMeta = document.querySelector("meta[name=csrf-token]")
    const csrf = csrfMeta?.content
    if (!csrf) return
    const body = new FormData()
    body.append("slide[aasm_state]", newState)
    const res = await fetch(this.destroyUrlValue, {
      method: "PATCH",
      headers: { "X-CSRF-Token": csrf, Accept: "text/vnd.turbo-stream.html" },
      body
    })
    if (!res.ok) {
      alert("Couldn't change the section's publication state.")
      select.value = previousState
      return
    }
    this.element.dataset.aasmState = newState
    this._restyleState(select, newState)
  }

  // Mirrors application_helper.rb#aasm_state_classes color buckets so
  // the toolbar select reads at-a-glance: gray for draft, green for
  // published, amber for unpublished.
  _restyleState(select, state) {
    select.classList.remove("sh-state-draft", "sh-state-published", "sh-state-unpublished")
    if (state === "published") select.classList.add("sh-state-published")
    else if (state === "unpublished") select.classList.add("sh-state-unpublished")
    else select.classList.add("sh-state-draft")
  }

  openEditor() {
    if (window.Turbo && document.querySelector("turbo-frame#slide-over")) {
      window.Turbo.visit(this.editUrlValue, { frame: "slide-over" })
    } else {
      window.location.href = this.editUrlValue
    }
  }

  openTypePalette() {
    // Toggle: a second click on Change type closes the popover instead of
    // stacking a new one.
    const existing = this.element.querySelector(":scope > [data-controller='admin-section-palette']")
    if (existing) { existing.remove(); return }
    const slideId = this.element.dataset.slideId
    const palette = this.element.dataset.palette
    const current = this.element.dataset.sectionType
    // PATCH and DELETE for a slide share the same path (/a/slides/:id) —
    // they differ only by HTTP method. Reuse destroyUrlValue so we don't
    // construct slide URLs in JS.
    const patchUrl = this.destroyUrlValue
    const popover = document.createElement("div")
    popover.setAttribute("data-controller", "admin-section-palette")
    popover.setAttribute("data-admin-section-palette-mode-value", "change-type")
    popover.setAttribute("data-admin-section-palette-slide-id-value", slideId)
    popover.setAttribute("data-admin-section-palette-palette-value", palette)
    popover.setAttribute("data-admin-section-palette-current-type-value", current)
    popover.setAttribute("data-admin-section-palette-patch-url-value", patchUrl)
    this.element.appendChild(popover)
  }

  async delete() {
    if (!confirm("Delete this section?")) return
    const csrfMeta = document.querySelector("meta[name=csrf-token]")
    const csrf = csrfMeta?.content
    if (!csrf) return
    const res = await fetch(this.destroyUrlValue, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrf, Accept: "text/vnd.turbo-stream.html" }
    })
    if (!res.ok) {
      alert("Couldn't delete that section.")
      return
    }
    // The server replies with a turbo_stream that removes the wrapper
    // by id. Render it through Turbo so any other instances of the
    // same slide on the page (e.g. multi-tab shared via turbo-cable)
    // disappear too. Fall back to optimistic local removal if the
    // response isn't a turbo stream (e.g. a 204 from an older server).
    const body = await res.text()
    if (window.Turbo && body.includes("<turbo-stream")) {
      window.Turbo.renderStreamMessage(body)
    } else {
      this.element.remove()
    }
  }
}
